<template>
  <div
    class="is-card"
    @keydown.enter="search"
  >
    <div class="filter__grid">
      <!-- document and name -->
      <base-input
        :value="filters.multi_search"
        type="text"
        placeholder="Buscar CPF, Nome ou nº Proposta"
        @change="filters.multi_search = String($event).trim()"
      >
        Cliente
      </base-input>

      <!-- ccb -->
      <base-input
        v-if="showAllFilters && !isContractManagement"
        :value="filters.external_proposal_id"
        type="text"
        placeholder="Número da CCB"
        @change="filters.external_proposal_id = String($event).trim()"
      >
        CCB
      </base-input>

      <!-- contract number -->
      <base-input
        v-if="showAllFilters && isContractManagement"
        :value="filters.contract_number"
        type="text"
        placeholder="Número do contrato"
        @change="filters.contract_number = String($event).trim()"
      >
        N° do contrato
      </base-input>

      <!-- status -->
      <base-select
        v-if="!isCreditor && !isContractManagement"
        v-model="filters.status"
        :options="statuses"
        :disabled="!statuses.length"
        can-filter
      >
        Status
      </base-select>

      <!-- Contract Management Status -->
      <base-select
        v-if="!isCreditor && isContractManagement"
        v-model="filters.contract_status"
        :options="[
          { name: 'Pago', value: 'pago' },
          { name: 'Em atraso', value: 'em_atraso' },
          { name: 'Pago parcialmente', value: 'pago_parcialmente' },
          { name: 'Em dia', value: 'em_dia' },
        ]"
        can-filter
      >
        Status
      </base-select>

      <!-- Assignee -->
      <base-select
        v-if="showAllFilters && isContractManagement"
        v-model="filters.assignee"
        :options="assigneesOptions"
        :disabled="!assigneesOptions.length"
        can-filter
      >
        Cessionário
      </base-select>

      <!-- promoter -->
      <base-select
        v-if="showAllFilters && !isCreditor && !isContractManagement && !isProductActionCLT"
        v-model="filters.promoter_id"
        :options="list.promoters"
        :searching="searching['promoter']"
        can-filter
        has-api-search
        @search="handlePromotersSearch($event, 'promoter')"
      >
        Promotor
      </base-select>

      <!-- responsible -->
      <base-select
        v-if="showAllFilters && !isCreditor && !isContractManagement && !isProductActionCLT"
        v-model="filters.responsible_id"
        :options="list.responsible"
        :searching="searching['responsible']"
        can-filter
        has-api-search
        @search="handleResponsibleSearch(
          $event,
          { ...(promoter_id ? { promoter_id } : '') },
          'responsible',
        )"
      >
        Responsável
      </base-select>

      <!-- Priority -->
      <base-select
        v-if="showAllFilters && isContractManagement"
        v-model="filters.priority_level"
        :options="[
          { name: 'Urgente', value: 'urgente' },
          { name: 'Alta', value: 'alta' },
          { name: 'Media', value: 'media' },
          { name: 'Baixa', value: 'baixa' },
        ]"
        can-filter
      >
        Prioridade
      </base-select>

      <!-- bank -->
      <base-select
        v-if="showAllFilters && !isProductCGV && !isContractManagement"
        v-model="filters.supplier_id"
        :options="suppliers"
        :disabled="!suppliers.length"
        can-filter
      >
        <span>Banco</span>
      </base-select>

      <!-- Week Day -->
      <base-select
        v-if="showAllFilters && isContractManagement"
        v-model="filters.week_day"
        :options="WEEK_DAYS"
        can-filter
      >
        Dias da semana
        <InfoIcon v-tooltip="'Filtra todos os contratos pagos no dia da semana selecionado.'" />
      </base-select>

      <!-- issue_type -->
      <base-select
        v-if="showAllFilters && !isContractManagement"
        v-model="filters.issue_type"
        :options="[
          { name: 'Financeiro', value: 'Financeiro' },
          { name: 'Formalização', value: 'Formalização' },
          { name: 'Problemas de averbação', value: 'Garantia' },
        ]"
        can-filter
      >
        Tipos de pendência
      </base-select>

      <!-- cnpj -->
      <base-select
        v-if="
          showAllFilters
            && canViewFilterCNPJ
            && isInternalUser
            && isProductCLT
            && !isContractManagement
        "
        v-model="filters.purchaser_document_number"
        :options="cnpjOptions"
        can-filter
      >
        <span>Comprador</span>
      </base-select>

      <!-- period -->
      <base-calendar
        v-model="form.period"
        class="period"
        @input="setPeriod"
      >
        Período
      </base-calendar>
    </div>

    <div
      :class="{ active: showAllFilters }"
      class="filter__switch"
    >
      <!-- antifraud -->
      <base-switch
        v-if="
          showAllFilters
            && !isProductCGV
            && !isContractManagement
        "
        v-model="filters.antifraud_completed"
      >
        <span>Formalizados</span>
      </base-switch>

      <!-- ccb -->
      <base-switch
        v-if="
          showAllFilters
            && !isProductCGV
            && !isContractManagement
        "
        v-model="filters.external_proposal_id_not_null"
      >
        <span>CCB Gerada</span>
      </base-switch>

      <!-- self contracting -->
      <base-switch
        v-if="
          showAllFilters
            && isProductFGTS
            && !isContractManagement
        "
        v-model="filters.self_contracting"
      >
        <span>Auto-contratação</span>
        <InfoIcon
          v-tooltip="'Propostas geradas através do link de autocontratação.'"
          class="ml-2"
        />
      </base-switch>

      <!-- self contracting -->
      <base-switch
        v-if="
          showAllFilters
            && isContractManagement
        "
        v-model="filters.with_shutdown"
      >
        <span>Apenas afastados</span>
        <InfoIcon
          v-tooltip="'Listas apenas clientes afastados ou desligados.'"
          class="ml-2"
        />
      </base-switch>
    </div>

    <div class="filter__actions">
      <btn-show-all v-model="showAllFilters" />
      <button
        class="button"
        @click="clean"
      >
        {{ isMobile ? 'Limpar' : 'Limpar todos os campos' }}
      </button>
      <button
        class="button is-success"
        @click="search"
      >
        Buscar
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import config from '@/data/config';
import { WEEK_DAYS } from '@/data/week_days';

import promoterPaginationMixin from '@/mixins/promoterPagination.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';

import BaseInput from '@/components/Base/Input/index.vue';
import BaseSelect from '@/components/Base/Select/index.vue';
import BaseCalendar from '@/components/BaseCalendar.vue';
import BtnShowAll from '@/components/Base/BtnShowAll/index.vue';
import BaseSwitch from '@/components/Base/Switch/index.vue';
import InfoIcon from '@/assets/icons/Info.vue';

export default {
  name: 'ProposalFilters',
  components: {
    BaseInput,
    BaseSelect,
    BaseCalendar,
    BtnShowAll,
    BaseSwitch,
    InfoIcon,
  },
  mixins: [
    promoterPaginationMixin,
    roleManagerMixin,
    loadingMixin,
  ],
  props: {
    productId: {
      type: [String, Number],
      required: true,
    },
    isContractManagement: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    WEEK_DAYS,
    form: {
      period: [],
    },
    list: {
      responsible: [],
      promoters: [],
    },
    toExclude: [
      'begin_date',
      'end_date',
    ],
    showAllFilters: false,
  }),
  computed: {
    ...mapGetters({
      isMobile: 'app/isMobile',
      assigneesOptions: 'contract/assigneesOptions',
      isInternalUser: 'user/isInternalUser',
    }),
    ...mapState({
      statuses: (state) => state.app.statuses,
      suppliers: (state) => state.app.suppliers,
      $_filters: (state) => state.proposal.filters,
    }),
    promoter_id() {
      return this.filters.promoter_id;
    },
    cnpjOptions() {
      return [
        { name: 'PB', value: '61820817000109' },
        { name: 'Prata', value: '62271128000147' },
      ];
    },
    filters: {
      get() {
        return this.$_filters;
      },
      set(value) {
        this.setFilterSearched(false);
        this.setFilters(value);
      },
    },
    hasFilters() {
      return Object
        .values(this.filters)
        .some((val) => Boolean(val));
    },

    // visivility
    canViewFilterCNPJ() {
      return [
        this.isSuper,
        this.isAntifraud,
        this.isCompliance,
      ].some((isValid) => isValid);
    },

    // product
    isProductFGTS() {
      return config.PRODUCT_ID_FGTS === this.productId;
    },
    isProductINSS() {
      return config.PRODUCT_ID_INSS === this.productId;
    },
    isProductCLT() {
      return config.PRODUCT_ID_WORKER_CREDIT === this.productId;
    },
    isProductCGV() {
      return config.PRODUCT_ID_CGV === this.productId;
    },
    isProductActionCLT() {
      return config.PRODUCT_ID_WORKER_CREDIT_ACTION === this.productId;
    },
  },
  watch: {
    'filters.end_date': {
      handler() {
        this.form.period = [
          this.filters.begin_date,
          this.filters.end_date,
        ];
      },
    },
    $_filters: {
      deep: true,
      handler() {
        this.setFilterSearched(false);
      },
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions({
      setFilters: 'proposal/SET_FILTERS',
      setFilterSearched: 'proposal/SET_FILTER_SEARCHED',
      resetFilters: 'proposal/RESET_FILTERS',
      getAssignees: 'contract/GET_ASSIGNEES',
    }),

    // handlers
    async loadData() {
      this.getAssignees();

      if (this.filters.begin_date && this.filters.end_date) {
        this.$set(this.form, 'period', [
          this.filters.begin_date,
          this.filters.end_date,
        ]);
      }

      if (this.hasFilters) {
        this.setFilterSearched(true);
      }
    },
    setPeriod([start, end]) {
      this.filters.begin_date = start;
      this.filters.end_date = end;
    },
    reset() {
      this.resetFilters();
      this.setFilterSearched(true);
    },
    paginate() {
      this.$emit('paginate');
      this.setFilterSearched(true);
    },
    clean() {
      this.reset();
      this.paginate();
    },
    search() {
      this.paginate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_buttonsVariables.scss';
@import '@/assets/scss/src/_textVariables.scss';

.filter {
  &__grid {
    display: grid;
    gap: 24px;

    > div {
      margin: 0;
    }
  }

  &__switch {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &.active {
      margin-top: 24px;
    }

    > div {
      margin: 0;
    }
  }

  &__actions {
    margin-top: 24px;
    display: grid;

    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
    gap: 1rem;

    > div {
      grid-column: span 2;
    }

    > button {
      width: 100%;
    }

    :deep(.divider-expand) {
      margin-bottom: 16px;
    }
  }
}

@media only screen and (min-width: 1024px) {
  .filter {
    &__grid {
      grid-template-columns: repeat(6, 1fr);
      align-items: end;

      > div {
        grid-column: span 2;

        // span 4
        &:nth-child(2):last-child,
        &:nth-child(5):last-child,
        &:nth-child(8):last-child {
          grid-column: span 4;
        }

        // span 6
        &:nth-child(10):last-child {
          grid-column: span 6;
        }
      }
    }

    &__switch {
      flex-direction: row;
    }

    &__actions {
      grid-template-columns: 1fr auto auto;
      grid-template-rows: 1fr;

      > div {
        grid-column: 1;
      }

      > button {
        width: fit-content;
      }

      :deep(.divider-expand) {
        margin-bottom: 0;
      }
    }
  }
}
</style>
